// E-Mail-OTP-Login (V1.14): zweistufige Anmeldung als Alternative zu Entra.
// Schritt 1: E-Mail → Code anfordern. Schritt 2: 6-stelligen Code prüfen.
// Bei Erfolg wird onAuthenticated() aufgerufen; der Aufrufer lädt dann /api/me
// neu bzw. wechselt in den eingeloggten Zustand.
import { useCallback, useEffect, useRef, useState } from 'react'
import { Icon } from '../../components/icons'
import { useBranding } from '../../branding/useBranding'
import { brandingLogoSrc } from '../../branding/logoSrc'
import { requestOtp, verifyOtp } from './otpActions'

const RESEND_SECONDS = 30
const CODE_LENGTH = 6

export interface OtpLoginProps {
  onAuthenticated: () => void
  // Optional: zurück zur Anmeldeauswahl (z.B. Entra-Option).
  onCancel?: () => void
}

type Step = 'email' | 'code'

export function OtpLogin({ onAuthenticated, onCancel }: OtpLoginProps) {
  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resendIn, setResendIn] = useState(0)
  const codeRef = useRef<HTMLInputElement>(null)

  // V1.14 White-Label: Branding-Logo oberhalb des Login-Formulars. Sicherster
  // Pfad analog AppShell — immer als <img> (XSS-sicher via brandingLogoSrc).
  // Ohne Logo bleibt das neutrale Mail-Icon als Fallback.
  const { appName, logo } = useBranding()
  const logoSrc = brandingLogoSrc(logo)

  // Resend-Countdown: tickt jede Sekunde herunter, bis 0.
  useEffect(() => {
    if (resendIn <= 0) return
    const t = setInterval(() => setResendIn((s) => (s <= 1 ? 0 : s - 1)), 1000)
    return () => clearInterval(t)
  }, [resendIn])

  const sendCode = useCallback(
    async (toEmail: string) => {
      setError(null)
      setBusy(true)
      try {
        await requestOtp(toEmail)
        setStep('code')
        setResendIn(RESEND_SECONDS)
      } catch {
        // requestOtp wirft normalerweise nicht (Backend immer 200); nur bei Netzfehler.
        setError('network')
      } finally {
        setBusy(false)
      }
    },
    [],
  )

  // Fokus aufs Code-Feld, sobald Schritt 2 sichtbar ist.
  useEffect(() => {
    if (step === 'code') codeRef.current?.focus()
  }, [step])

  const onEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim() || busy) return
    void sendCode(email.trim())
  }

  const onResend = () => {
    if (resendIn > 0 || busy) return
    void sendCode(email.trim())
  }

  const onCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy || code.length !== CODE_LENGTH) return
    setError(null)
    setBusy(true)
    try {
      await verifyOtp(email.trim(), code)
      onAuthenticated()
    } catch {
      setError('otp_invalid')
      setCode('')
      codeRef.current?.focus()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="vitals" style={{ display: 'flex', flexDirection: 'column', minHeight: '100%', background: 'var(--cream)' }}>
      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 40px 80px' }}>
        <div style={{ width: '100%', maxWidth: 380 }}>
          <div style={{ textAlign: 'center', marginBottom: 22 }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--surface)', border: '1px solid var(--line)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18, color: 'var(--viridian)',
              boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
              {logoSrc ? (
                // Branding-Logo — immer als <img> eingebettet (XSS-sicher).
                <img src={logoSrc} alt={appName} style={{ maxWidth: 40, maxHeight: 40, objectFit: 'contain', display: 'block' }} />
              ) : (
                <Icon.mail size={26} />
              )}
            </div>
            {step === 'email' ? (
              <>
                <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--woodland)', letterSpacing: '-0.3px' }}>Anmelden · E-Mail eingeben</div>
                <p style={{ fontSize: 13.5, color: 'var(--ink-50)', marginTop: 8 }}>Wir senden dir einen einmaligen Anmeldecode.</p>
              </>
            ) : (
              <>
                <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--woodland)', letterSpacing: '-0.3px' }}>Prüf deine E-Mail</div>
                <p style={{ fontSize: 13.5, color: 'var(--ink-50)', marginTop: 8 }}>
                  Wenn für <strong style={{ color: 'var(--ink-70)' }}>{email.trim()}</strong> ein aktiver Zugang besteht, haben wir dir einen Code gesendet.
                </p>
              </>
            )}
          </div>

          {step === 'email' ? (
            <form onSubmit={onEmailSubmit} className="vt-field" aria-label="E-Mail-Anmeldung">
              <label className="vt-label" htmlFor="otp-email">E-Mail-Adresse</label>
              <input
                id="otp-email"
                className="vt-input"
                type="email"
                autoComplete="email"
                autoFocus
                inputMode="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {error === 'network' && (
                <div role="alert" style={{ fontSize: 13, color: 'var(--st-offline)', marginTop: 2 }}>
                  Senden fehlgeschlagen. Bitte versuche es erneut.
                </div>
              )}
              <button type="submit" className="vt-btn vt-btn-primary" style={{ justifyContent: 'center', marginTop: 6 }} disabled={busy || !email.trim()}>
                {busy ? 'Senden…' : 'Code senden'}
              </button>
            </form>
          ) : (
            <form onSubmit={onCodeSubmit} className="vt-field" aria-label="Code-Eingabe">
              <label className="vt-label" htmlFor="otp-code">Anmeldecode</label>
              <input
                id="otp-code"
                ref={codeRef}
                className="vt-input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={CODE_LENGTH}
                placeholder="000000"
                aria-invalid={error === 'otp_invalid' || undefined}
                value={code}
                onChange={(e) => {
                  setError(null)
                  setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))
                }}
                style={{ textAlign: 'center', letterSpacing: '0.5em', fontVariantNumeric: 'tabular-nums', fontSize: 20 }}
              />
              {error === 'otp_invalid' && (
                <div role="alert" style={{ fontSize: 13, color: 'var(--st-offline)', marginTop: 2 }}>
                  Der Code ist ungültig oder abgelaufen. Bitte prüfe ihn.
                </div>
              )}
              <button type="submit" className="vt-btn vt-btn-primary" style={{ justifyContent: 'center', marginTop: 6 }} disabled={busy || code.length !== CODE_LENGTH}>
                {busy ? 'Prüfen…' : 'Anmelden'}
              </button>
              <button
                type="button"
                className="vt-btn vt-btn-ghost"
                style={{ justifyContent: 'center' }}
                onClick={onResend}
                disabled={resendIn > 0 || busy}
              >
                {resendIn > 0 ? `Erneut senden in ${resendIn} s` : 'Code erneut senden'}
              </button>
              {/* L7: Gesperrte/wartende Logins bekommen keinen Code (Anti-Enumeration) — Ausweg nennen. */}
              <p style={{ fontSize: 12.5, color: 'var(--ink-50)', marginTop: 4, textAlign: 'center' }}>
                Kein Code angekommen? Ist dein Zugang gesperrt oder noch nicht freigeschaltet, senden wir keinen Code — wende dich dann an deine IT.
              </p>
            </form>
          )}

          {onCancel && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button type="button" className="vt-btn vt-btn-ghost" style={{ fontSize: 13 }} onClick={onCancel}>
                Andere Anmeldeoption
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
